// Visitor analytics types.
//
// ⚑ This file holds TWO shapes per concept, and the names collide with the C# ones in a way that
// will mislead a reader who does not know why.
//
// The `Raw*` types ARE the wire, and are generated from OrbitalX.Dto — see api-generated.d.ts.
// Everything else is the VIEW MODEL the tiles consume, produced by `normalizeAnalyticsView`
// (src/utils/analyticsNormalize.ts). Three of those view-model names — AnalyticsViewDto,
// EngagementMetricsDto, UnknownReportingDto — are also C# type names, and the C# types of those
// names are the Raw shapes below, not these. Do not "fix" a view-model type by re-pointing it at
// the generated type of the same name; they describe different objects.
//
// ORB-328 (P2) shipped the baseline funnel counts (LandingPageViews / ThankYouPageViews /
// Conversions). ORB-373 (P3 · M1) layers on richer per-level metrics, two attribution views,
// aggregate unknown reporting, and per-Contact timelines. The M1 additions on the view model are
// optional so the P2 surfaces keep compiling while the M1 API rolls out.

export type {
  AnalyticsMetricDto,
  AnalyticsViewDto as RawAnalyticsViewDto,
  AnalyticsBreakdownItemDto as RawAttributionRowDto,
  AttributionDto as RawAttributionDto,
  ContactVisitorRollupDto,
  ContactVisitorTimelineDto,
  EngagementMetricsDto as RawEngagementMetricsDto,
  PageViewDetailDto as ContactPageViewDto,
  UnknownReportDto as RawUnknownReportingDto,
  VisitorMetricsDto as RawVisitorMetricsDto,
  VisitorSessionTimelineItemDto as ContactSessionDto,
} from './api-generated'

import type { AnalyticsMetricDto } from './api-generated'

/**
 * Attribution view selector (ORB-373). Flips how sessions/conversions are credited:
 *  - `ConversionSession` (default): the converting session's own first-touch
 *    (UTM → referrer → Direct).
 *  - `FirstSeen`: the visitor's first-ever session first-touch.
 *
 * A UI-side label set, not a wire value: `AttributionDto.View` is a C# enum property and travels as
 * an INTEGER, which is why the generated type says `number` and analyticsNormalize maps it by index.
 */
export type AttributionView = 'ConversionSession' | 'FirstSeen'

export const ATTRIBUTION_VIEWS: { value: AttributionView; label: string }[] = [
  { value: 'ConversionSession', label: 'Conversion session' },
  { value: 'FirstSeen', label: 'First seen' },
]

/** Marketing channel buckets derived from UTM medium + referrer. */
export type AnalyticsChannel = 'Email' | 'Paid' | 'Social' | 'Organic' | 'Direct'

/**
 * One row in a top-N attribution list (top referrers, top UTM campaigns, sessions by
 * source/medium/campaign). `Sessions` is the primary count; `Conversions` is included
 * when the API credits conversions to the row. Normalizer output — the wire row is
 * `RawAttributionRowDto` (C#: AnalyticsBreakdownItemDto), a bare Key/Count pair.
 */
export interface AttributionItemDto {
  Label: string
  Sessions: AnalyticsMetricDto
  Conversions?: AnalyticsMetricDto
}

/** Sessions split across the five marketing channels. Each bucket is range-aware. */
export interface ChannelBreakdownDto {
  Email: AnalyticsMetricDto
  Paid: AnalyticsMetricDto
  Social: AnalyticsMetricDto
  Organic: AnalyticsMetricDto
  Direct: AnalyticsMetricDto
}

/**
 * Aggregate unknown-visitor reporting (ORB-373). Counts only — no individual unknown
 * profiles are ever surfaced. `ConversionRate.Current` is the unknown→known conversion
 * rate as a percentage (0–100). Normalizer output; the wire shape is RawUnknownReportingDto.
 */
export interface UnknownReportingDto {
  UniqueVisitors: AnalyticsMetricDto
  Sessions: AnalyticsMetricDto
  ConversionRate: AnalyticsMetricDto
}

/**
 * One discrete scroll-depth milestone in the engagement funnel (ORB-374). `ThresholdPct`
 * is the milestone (25/50/75/100); `Events` counts the `scroll_depth` events that crossed it.
 * This is the milestone funnel — distinct from {@link EngagementMetricsDto.AvgMaxScrollPct},
 * the per-session deepest scroll — so the two are never double-counted.
 */
export interface ScrollDepthBucketDto {
  ThresholdPct: number
  Events: AnalyticsMetricDto
}

/**
 * Per-scope engagement metrics (ORB-374 · M2), derived from the engagement event types.
 * Time-on-page and per-session max scroll come from the once-per-pageview `page_engagement`
 * dwell summary; the scroll funnel from the discrete `scroll_depth` milestones; the
 * interaction counts from `cta_click` / `download_click` / `video_play`. Like the M1 visitor
 * metrics these are current-window only (no PreviousWindow/Delta), so the tiles render
 * without a growth pill.
 *
 * Normalizer output. The C# type of this name is the wire shape, re-exported above as
 * RawEngagementMetricsDto.
 */
export interface EngagementMetricsDto {
  /** Mean time-on-page in seconds across `page_engagement` dwell records. 0 until beacons land. */
  AvgTimeOnPageSeconds: AnalyticsMetricDto
  /**
   * Mean per-session deepest scroll as a percentage (0–100), from `page_engagement.maxScrollPct`,
   * over sessions that actually scrolled. ORB-534: sessions with a real maximum of 0 are excluded —
   * averaging them in described the non-reading population rather than the readers. Read it with
   * {@link ScrolledSessions}; a high average over a handful of sessions overstates.
   */
  AvgMaxScrollPct: AnalyticsMetricDto
  /** Sessions that scrolled — the denominator behind {@link AvgMaxScrollPct}. Absent on an older API. */
  ScrolledSessions?: number
  /** Scroll funnel: `scroll_depth` milestone counts, ordered by threshold ascending. */
  ScrollDepthFunnel: ScrollDepthBucketDto[]
  /** `cta_click` event count in scope. */
  CtaClicks: AnalyticsMetricDto
  /** `download_click` event count in scope. */
  DownloadClicks: AnalyticsMetricDto
  /** `video_play` event count in scope. */
  VideoPlays: AnalyticsMetricDto
  /** Number of `page_engagement` dwell records — the denominator behind the dwell metrics. */
  PageEngagementEvents: number
}

/**
 * Composite analytics **view model** consumed by the tiles. NOT the wire shape: the API nests the
 * M1 visitor section differently (see RawAnalyticsViewDto), and `normalizeAnalyticsView` maps wire →
 * this. The P2 funnel fields match the wire 1:1; the M1 fields are produced by the normalizer.
 */
export interface AnalyticsViewDto {
  // --- P2 baseline funnel (ORB-328) ---
  LandingPageViews: AnalyticsMetricDto
  /**
   * Distinct sessions that viewed the page (ORB-534). Optional: absent until the matching API
   * change is deployed to this environment, and the funnel falls back to the old Thank-You stage
   * while it is, so a dashboard that ships first degrades rather than rendering a zero.
   */
  UniqueLandingPageViews?: AnalyticsMetricDto
  ThankYouPageViews: AnalyticsMetricDto
  Conversions: AnalyticsMetricDto

  // --- M1 richer metrics (ORB-373) — optional during API rollout ---
  /** Distinct visitors across the window. */
  UniqueVisitors?: AnalyticsMetricDto
  /** Distinct visitors with a resolved ContactId. */
  UniqueKnownVisitors?: AnalyticsMetricDto
  /** Distinct visitors with no ContactId. */
  UniqueUnknownVisitors?: AnalyticsMetricDto
  /** Distinct visitors with a prior session on a persistent ID. */
  ReturningVisitors?: AnalyticsMetricDto
  /** Total visitor sessions. */
  Sessions?: AnalyticsMetricDto
  /** Conversion rate as a percentage (0–100). */
  ConversionRate?: AnalyticsMetricDto
  /**
   * Average session duration in seconds. Single-pageview (bounce) sessions read as 0
   * until M2 dwell beacons land.
   */
  AvgSessionDurationSeconds?: AnalyticsMetricDto

  /** Top traffic referrers by session count, highest first. */
  TopReferrers?: AttributionItemDto[]
  /** Top UTM campaigns by session count, highest first. */
  TopUtmCampaigns?: AttributionItemDto[]
  /** Sessions bucketed across the five marketing channels. */
  Channels?: ChannelBreakdownDto

  /** Aggregate unknown-visitor reporting. */
  Unknown?: UnknownReportingDto

  /** Engagement metrics (ORB-374 · M2): time-on-page, scroll depth, click/CTA/download/video. */
  Engagement?: EngagementMetricsDto

  /** Echoes which attribution view produced the credited numbers above. */
  AttributionView?: AttributionView
}
